import { AxiosResponse } from 'axios'
import { ResponseSuccess } from 'integration/types'
import { Platform } from 'react-native'
import { theme } from 'src/theme'
import { currencyFormatChart } from 'src/utils/currencyFormat'

import {
  RangeInfo,
  RangeInfoSerialized,
  RvDescriptionItem,
  RvDescriptionItemSerialized,
  RvSummary,
  RvSummarySerialized,
  TIndicator
} from './types'
import { BUSINESS_CODE } from '../auth'

export const KEY_GET_RV_SUMMARY_QUERY = 'useGetRVSummaryQuery'

type ProfileConfig = {
  footer_text: string
  earnings: {
    indicators: { [key: string]: { bullet_color?: string; key?: string } }
  }
  statement: {
    indicators: {
      progress_bar: {
        points: {
          [key: string]: {
            placement: 'top' | 'bottom'
            has_line_indicator?: boolean
            indicator_color?: string
            title: string
          }
        }
      }
      description?: { [key: string]: { slug_color?: string } }
      bottom_description?: { [key: string]: { slug_color?: string } }
    }
  }
}

const profileConfigs: Partial<Record<BUSINESS_CODE, ProfileConfig>> = {
  [BUSINESS_CODE.GOODS_AND_SERVICES]: {
    footer_text:
      ' Os valores aqui demostrados devem ser utilizados para simples estimativas e não consideram eventuais proporcionalidades nem representam critérios oficiais de apuração e pagamento',
    earnings: {
      indicators: {
        'Produção Projetada': { bullet_color: theme.colors.secondary[50] },
        'Ganho Projetado': { bullet_color: theme.colors.secondary[800] },
        'Gatilho 1': { bullet_color: theme.colors['tabs-yellow'][50], key: 'G1' },
        'Gatilho 2': { bullet_color: theme.colors.secondary[500], key: 'G2' },
        'Gatilho 3': { bullet_color: theme.colors.tertiary[500], key: 'G3' }
      }
    },
    statement: {
      indicators: {
        progress_bar: {
          points: {
            G1: {
              placement: 'top',
              has_line_indicator: true,
              title: 'Gatilho 1'
            },
            G2: {
              placement: 'top',
              has_line_indicator: true,
              title: 'Gatilho 2'
            },
            G3: {
              placement: 'top',
              has_line_indicator: true,
              title: 'Gatilho 3'
            }
          }
        },
        bottom_description: {
          G1: { slug_color: theme.colors['tabs-yellow'][50] },
          G2: { slug_color: theme.colors.secondary[500] },
          G3: { slug_color: theme.colors.tertiary[500] }
        }
      }
    }
  },
  [BUSINESS_CODE.LEVES]: {
    footer_text:
      'Os valores "Produção, Baseline, Fidelização, Seguro SPF e Seguro Auto" devem ser utilizados para simples estimativas e para esclarecer eventuais pontos sobre o cálculo de comissionamento. Ele não considera eventuais proporcionalidades nem representa critérios oficiais de apuração e pagamento.\n\nEsse ganho atual é superestimado devido ao valor da fidelização e por não estar levando em consideração o critério do over90.',
    earnings: {
      indicators: {
        Produção: { bullet_color: theme.colors.secondary[500] },
        Baseline: { bullet_color: theme.colors.tertiary[500], key: 'Baseline' }
      }
    },
    statement: {
      indicators: {
        progress_bar: {
          points: {
            Baseline: {
              placement: 'top',
              has_line_indicator: true,
              indicator_color: theme.colors.tertiary[500],
              title: 'Baseline'
            }
          }
        },
        description: {
          Baseline: { slug_color: theme.colors.tertiary[500] }
        }
      }
    }
  },
  [BUSINESS_CODE.PESADOS]: {
    footer_text:
      'Os valores "Produção, Baseline e Gatilho" devem ser utilizados para simples estimativas e para esclarecer eventuais pontos sobre o cálculo de comissionamento. Ele não considera eventuais proporcionalidades nem representa critérios oficiais de apuração e pagamento.\n\nEsse ganho atual é superestimado devido ao valor da fidelização e por não estar levando em consideração o critério do over90.',
    earnings: {
      indicators: {
        Produção: { bullet_color: theme.colors.secondary[500] },
        Baseline: { bullet_color: theme.colors['tabs-yellow'][50], key: 'Baseline' },
        'Gatilho 1': { bullet_color: theme.colors.secondary[500], key: 'G1' },
        'Gatilho 2': { bullet_color: theme.colors.tertiary[500], key: 'G2' }
      }
    },
    statement: {
      indicators: {
        progress_bar: {
          points: {
            Baseline: {
              placement: 'top',
              has_line_indicator: true,
              indicator_color: theme.colors.secondary[500],
              title: 'Baseline'
            },
            G1: {
              placement: 'bottom',
              has_line_indicator: true,
              title: 'Gatilho 1'
            },
            G2: {
              placement: 'bottom',
              title: 'Gatilho 2'
            }
          }
        },
        description: {
          Baseline: { slug_color: theme.colors['tabs-yellow'][50] },
          G1: { slug_color: theme.colors.secondary[500] },
          G2: { slug_color: theme.colors.tertiary[500] }
        }
      }
    }
  }
}

const getProfileConfig = (businessCode: BUSINESS_CODE): ProfileConfig =>
  profileConfigs[businessCode] ?? profileConfigs[BUSINESS_CODE.GOODS_AND_SERVICES]!

const serializeEarningsIndicators = (
  indicators: TIndicator[],
  businessCode: BUSINESS_CODE
): RvSummarySerialized['resume']['earnings']['indicators'] => {
  const config = getProfileConfig(businessCode)

  const isMobile = Platform.OS !== 'web'

  return indicators.map((indicator) => {
    const configItem = config.earnings.indicators[indicator.title]

    const shouldFormatWithChart =
      businessCode === BUSINESS_CODE.GOODS_AND_SERVICES &&
      isMobile &&
      configItem?.key &&
      ['G1', 'G2', 'G3'].includes(configItem.key) &&
      typeof indicator.value === 'number'

    return {
      ...indicator,
      value: shouldFormatWithChart
        ? currencyFormatChart(String(indicator.value), true)
        : indicator.value,
      bullet_color: configItem?.bullet_color,
      key: configItem?.key
    }
  })
}

const serializeStatementProgressBarPoints = (
  points: RvSummary['statement']['indicators'][0]['progress_bar']['points'],
  businessCode: BUSINESS_CODE,
  indicatorIndex: number
): RvSummarySerialized['statement']['indicators'][0]['progress_bar']['points'] => {
  const config = getProfileConfig(businessCode)

  return points.map((point, index) => {
    let configKey = point.slug

    if (businessCode === BUSINESS_CODE.LEVES && indicatorIndex > 0) {
      configKey = index === 0 ? 'G1' : 'G2'
    }

    const baseConfig = config.statement.indicators.progress_bar.points[configKey] || {}

    const bulletColor =
      config.statement.indicators.bottom_description?.[configKey]?.slug_color ??
      config.statement.indicators.description?.[configKey]?.slug_color

    return {
      ...point,
      ...baseConfig,
      bullet_color: bulletColor
    }
  })
}

const serializeDescriptionItems = (
  items: RvDescriptionItem[],
  configItems: Record<string, { slug_color?: string }>,
  businessCode: BUSINESS_CODE,
  indicatorIndex: number
): RvDescriptionItemSerialized[] => {
  return items.map((item) => ({
    ...item,
    slug_color: item.slug ? configItems[item.slug]?.slug_color : undefined
  }))
}

const serializeStatementIndicators = (
  indicators: RvSummary['statement']['indicators'],
  businessCode: BUSINESS_CODE
): RvSummarySerialized['statement']['indicators'] => {
  const config = getProfileConfig(businessCode)

  return indicators.map((indicator, index) => ({
    ...indicator,
    progress_bar: {
      ...indicator.progress_bar,
      current_progress: Number(indicator?.progress_bar?.current_progress?.toFixed(2)) ?? 0,
      points: serializeStatementProgressBarPoints(
        indicator.progress_bar.points,
        businessCode,
        index
      )
    },
    description: serializeDescriptionItems(
      indicator.description,
      config.statement.indicators.description ?? {},
      businessCode,
      index
    ),
    bottom_description: indicator.bottom_description
      ? serializeDescriptionItems(
          indicator.bottom_description,
          config.statement.indicators.bottom_description ?? {},
          businessCode,
          index
        )
      : undefined
  }))
}

const isEmpty = (obj?: any): boolean => {
  if (!obj) return true

  return Object.keys(obj).length === 0
}

const serializeRangeInfo = (rangeInfo?: RangeInfo): RangeInfoSerialized | undefined => {
  if (!rangeInfo) return undefined

  const ranges =
    !isEmpty(rangeInfo?.ranges) &&
    (!isEmpty(rangeInfo?.ranges?.range_a) || !isEmpty(rangeInfo?.ranges?.range_b))
      ? {
          range_a: !isEmpty(rangeInfo?.ranges?.range_a) ? rangeInfo?.ranges?.range_a : null,
          range_b: !isEmpty(rangeInfo?.ranges?.range_b) ? rangeInfo?.ranges?.range_b : null
        }
      : null

  const projected_gains =
    !isEmpty(rangeInfo?.projected_gains) &&
    (!isEmpty(rangeInfo?.projected_gains?.range_a) || !isEmpty(rangeInfo?.projected_gains?.range_b))
      ? {
          range_a: !isEmpty(rangeInfo?.projected_gains?.range_a)
            ? rangeInfo?.projected_gains?.range_a
            : null,
          range_b: !isEmpty(rangeInfo?.projected_gains?.range_b)
            ? rangeInfo?.projected_gains?.range_b
            : null
        }
      : null

  if (!ranges && !projected_gains) return undefined

  return {
    ranges,
    projected_gains
  }
}

const calculateTriggerValues = (indicators: any[], businessCode: BUSINESS_CODE) => {
  if (businessCode === BUSINESS_CODE.GOODS_AND_SERVICES) {
    const g1 = Number(indicators.find((i) => i.title === 'Gatilho 1')?.value ?? 0)

    const g2 = Number(indicators.find((i) => i.title === 'Gatilho 2')?.value ?? 0)

    const g3 = Number(indicators.find((i) => i.title === 'Gatilho 3')?.value ?? 0)

    const total = g3 * 1.15

    return {
      g1,
      g2,
      g3,
      total,
      g1Percent: total > 0 ? (g1 / total) * 100 : 0,
      g2Percent: total > 0 ? (g2 / total) * 100 : 0,
      g3Percent: total > 0 ? (g3 / total) * 100 : 0
    }
  } else if (businessCode === BUSINESS_CODE.LEVES) {
    const baseline = Number(indicators.find((i) => i.title === 'Baseline')?.value ?? 0)

    const total = baseline * 1.15

    return {
      baseline,
      total,
      baselinePercent: total > 0 ? (baseline / total) * 100 : 0
    }
  } else if (businessCode === BUSINESS_CODE.PESADOS) {
    const baseline = Number(indicators.find((i) => i.title === 'Baseline')?.value ?? 0)

    const g1 = Number(indicators.find((i) => i.title === 'Gatilho 1')?.value ?? 0)

    const g2 = Number(indicators.find((i) => i.title === 'Gatilho 2')?.value ?? 0)

    const total = g2 * 1.15

    return {
      baseline,
      g1,
      g2,
      total,
      baselinePercent: total > 0 ? (baseline / total) * 100 : 0,
      g1Percent: total > 0 ? (g1 / total) * 100 : 0,
      g2Percent: total > 0 ? (g2 / total) * 100 : 0
    }
  }
}

const calculateArcFills = (earnings: any, triggerValues: any) => {
  const realizedValue = Number(earnings?.realized_value ?? 0)

  const productionValue =
    earnings?.production_value !== undefined
      ? Number(earnings.production_value)
      : realizedValue * 100

  const projected =
    earnings?.indicators?.find((i: any) => i.title === 'Produção Projetada')?.value ?? 0

  const arcFill = triggerValues.total > 0 ? (productionValue / triggerValues.total) * 100 : 0

  const targetFill = triggerValues.total > 0 ? (Number(projected) / triggerValues.total) * 100 : 0

  return {
    productionValue,
    arcFill,
    targetFill
  }
}

export const getRvSummary = (
  response: AxiosResponse<ResponseSuccess<RvSummary>>,
  businessCode: BUSINESS_CODE
): AxiosResponse<ResponseSuccess<RvSummarySerialized>> => {
  const originalEarnings = response.data.data.resume.earnings

  const triggerValues = calculateTriggerValues(originalEarnings.indicators, businessCode)

  const arcValues = calculateArcFills(originalEarnings, triggerValues)

  const serializedData: RvSummarySerialized = {
    ...response.data.data,
    resume: {
      ...response.data.data.resume,
      earnings: {
        ...originalEarnings,
        production_value: originalEarnings.production_value,
        footer_text: getProfileConfig(businessCode).footer_text,
        indicators: serializeEarningsIndicators(originalEarnings.indicators, businessCode),

        calculated_values: {
          triggers: {
            baseline: triggerValues?.baseline,
            g1: triggerValues?.g1,
            g2: triggerValues?.g2,
            g3: triggerValues?.g3,
            total: triggerValues?.total ?? 0,
            baselinePercent: triggerValues?.baselinePercent,
            g1Percent: triggerValues?.g1Percent,
            g2Percent: triggerValues?.g2Percent,
            g3Percent: triggerValues?.g3Percent
          },
          arc: {
            productionValue: arcValues.productionValue,
            arcFill: arcValues.arcFill,
            targetFill: arcValues.targetFill
          }
        }
      }
    },
    statement: {
      ...response.data.data.statement,
      indicators: serializeStatementIndicators(
        response.data.data.statement.indicators,
        businessCode
      )
    },
    range_info: serializeRangeInfo(response?.data?.data?.range_info)
  }

  return {
    ...response,
    data: {
      ...response.data,
      data: serializedData
    }
  }
}
